import { convertToBinLevelTimeSeries } from '../chartHelpers';
import { aggregateSeriesToTvPoints } from './hourlySeriesEncoding';
import {
  applyInitialAnchor,
  bridgeTailToAnchor,
  buildFeedEventsFromHourlySummaries,
  buildResetOverrides,
  toVisxSeries,
} from './calibrationSeriesUtils';

/**
 * Build the live data tail past the last cached calibrated inventory point.
 *
 * @param {{
 *   cachedCalibratedDataPoints: { t: number, v: number }[],
 *   binSetLevels: { valid_at: number, level_in_grams: number, purpose?: string }[],
 *   feedLines: { feed_frame_summaries_v2?: { end_timestamp: number, total_consumption: number }[] }[],
 *   deliveries: { occurredAt: number, value: number }[],
 *   binSetCapacityInGrams: number,
 *   nowTs: number,
 * }} params
 * @returns {{ t: number, v: number }[]}
 */
export default function buildCalibrationSeriesTail({
  cachedCalibratedDataPoints = [],
  binSetLevels = [],
  feedLines = [],
  deliveries = [],
  binSetCapacityInGrams,
  nowTs,
}) {
  const sortedCached = [...cachedCalibratedDataPoints].sort((a, b) => a.t - b.t);
  const lastCached = sortedCached.at(-1);

  let initialAnchor;
  if (lastCached) {
    initialAnchor = { occurredAt: lastCached.t, value: lastCached.v };
  } else {
    const firstReset = buildResetOverrides(binSetLevels)[0] ?? null;
    if (!firstReset) {
      return [];
    }
    initialAnchor = firstReset;
  }

  const resetOverrides = buildResetOverrides(binSetLevels);
  const binOverrides = applyInitialAnchor(resetOverrides, initialAnchor);
  if (!binOverrides.length) {
    return [];
  }

  const feedEvents = buildFeedEventsFromHourlySummaries(feedLines, initialAnchor.occurredAt);
  const tailDeliveries = deliveries.filter((d) => d.occurredAt > initialAnchor.occurredAt);

  const ledger = convertToBinLevelTimeSeries({
    feedEvents,
    deliveries: tailDeliveries,
    binOverrides,
    binSetCapacityInGrams,
    debugMode: true,
    skipPreOverrideBackfill: true,
  });

  const sparsePoints = toVisxSeries(ledger);
  const encoded = aggregateSeriesToTvPoints(
    sparsePoints.map((p) => ({ event_time: p.t, rolling_inventory: p.v })),
    { nowTs, trimFuture: true },
  );

  const bridgeAnchor = lastCached ?? { t: initialAnchor.occurredAt, v: initialAnchor.value };
  return bridgeTailToAnchor(encoded, bridgeAnchor);
}
